<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FlowMediaAssetsAPI from 'dashboard/api/flowMediaAssets';
import { slugify } from './utils/nodeFactory';

const props = defineProps({
  node: { type: Object, default: null },
});

const emit = defineEmits(['updateData', 'renameHandle', 'removeHandle', 'deleteNode', 'close']);

const { t } = useI18n();

const data = computed(() => props.node?.data || { title: '', config: {} });
const config = computed(() => data.value.config || {});

const patch = (partialData) => {
  emit('updateData', {
    nodeId: props.node.id,
    data: JSON.parse(JSON.stringify({ ...data.value, ...partialData })),
  });
};

const patchConfig = (partialConfig) => {
  patch({ config: { ...config.value, ...partialConfig } });
};

// ---- whatsapp_list helpers -------------------------------------------------
const updateSection = (index, partial) => {
  const sections = JSON.parse(JSON.stringify(config.value.sections || []));
  sections[index] = { ...sections[index], ...partial };
  patchConfig({ sections });
};

const updateRow = (sectionIndex, rowIndex, partial) => {
  const sections = JSON.parse(JSON.stringify(config.value.sections || []));
  const row = sections[sectionIndex].rows[rowIndex];
  if (partial.id !== undefined && partial.id !== row.id) {
    emit('renameHandle', {
      nodeId: props.node.id,
      from: `row-${row.id}`,
      to: `row-${partial.id}`,
    });
  }
  sections[sectionIndex].rows[rowIndex] = { ...row, ...partial };
  patchConfig({ sections });
};

const addRow = sectionIndex => {
  const sections = JSON.parse(JSON.stringify(config.value.sections || []));
  const count = sections.flatMap(s => s.rows || []).length + 1;
  sections[sectionIndex].rows = sections[sectionIndex].rows || [];
  sections[sectionIndex].rows.push({ id: `opcao_${count}_${Date.now().toString(36)}`, title: '', description: '' });
  patchConfig({ sections });
};

const removeRow = (sectionIndex, rowIndex) => {
  const sections = JSON.parse(JSON.stringify(config.value.sections || []));
  const [removed] = sections[sectionIndex].rows.splice(rowIndex, 1);
  if (removed) emit('removeHandle', { nodeId: props.node.id, handle: `row-${removed.id}` });
  patchConfig({ sections });
};

const addSection = () => {
  const sections = JSON.parse(JSON.stringify(config.value.sections || []));
  sections.push({ title: '', rows: [] });
  patchConfig({ sections });
};

const removeSection = sectionIndex => {
  const sections = JSON.parse(JSON.stringify(config.value.sections || []));
  const [removed] = sections.splice(sectionIndex, 1);
  (removed?.rows || []).forEach(row =>
    emit('removeHandle', { nodeId: props.node.id, handle: `row-${row.id}` })
  );
  patchConfig({ sections });
};

// ---- buttons helpers -------------------------------------------------------
const updateButton = (index, partial) => {
  const buttons = JSON.parse(JSON.stringify(config.value.buttons || []));
  const button = buttons[index];
  if (partial.id !== undefined && partial.id !== button.id) {
    emit('renameHandle', {
      nodeId: props.node.id,
      from: `btn-${button.id}`,
      to: `btn-${partial.id}`,
    });
  }
  buttons[index] = { ...button, ...partial };
  patchConfig({ buttons });
};

const addButton = () => {
  const buttons = JSON.parse(JSON.stringify(config.value.buttons || []));
  if (buttons.length >= 3) return;
  buttons.push({ id: `botao_${buttons.length + 1}_${Date.now().toString(36)}`, title: '' });
  patchConfig({ buttons });
};

const removeButton = index => {
  const buttons = JSON.parse(JSON.stringify(config.value.buttons || []));
  const [removed] = buttons.splice(index, 1);
  if (removed) emit('removeHandle', { nodeId: props.node.id, handle: `btn-${removed.id}` });
  patchConfig({ buttons });
};

// ---- handoff labels --------------------------------------------------------
const labelsText = computed(() => (config.value.labels || []).join(', '));
const updateLabels = value => {
  patchConfig({
    labels: value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean),
  });
};

const autoId = title => slugify(title);

// ---- media helpers ----------------------------------------------------------
const isUploading = ref(false);
const MEDIA_ACCEPT = {
  image: 'image/jpeg,image/png,image/webp',
  audio: 'audio/mpeg,audio/ogg,audio/mp4,audio/aac,audio/amr',
  video: 'video/mp4,video/3gpp',
  file: 'application/pdf,text/csv,text/plain,.doc,.docx,.xls,.xlsx',
};

const onMediaTypeChange = value => {
  patchConfig({ media_type: value, media_asset_id: null, filename: '' });
};

const uploadMedia = async event => {
  const file = event.target.files && event.target.files[0];
  if (!file) return;
  isUploading.value = true;
  try {
    const { data } = await FlowMediaAssetsAPI.upload(
      file,
      config.value.media_type || 'image'
    );
    patchConfig({ media_asset_id: data.id, filename: data.filename });
    useAlert(t('FLOWS.BUILDER.PANEL.MEDIA_UPLOADED'));
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t('FLOWS.API.ERROR');
    useAlert(message);
  } finally {
    isUploading.value = false;
    event.target.value = '';
  }
};

const removeMedia = () => patchConfig({ media_asset_id: null, filename: '' });
</script>

<template>
  <aside
    v-if="node && node.type !== 'start'"
    class="w-80 shrink-0 border-l border-n-weak bg-n-solid-1 overflow-y-auto"
  >
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ $t(`FLOWS.BUILDER.NODES.${node.type.toUpperCase()}`) }}
      </span>
      <div class="flex gap-1">
        <button
          v-tooltip.top="$t('FLOWS.BUILDER.PANEL.DELETE_NODE')"
          type="button"
          class="p-1 rounded text-n-ruby-11 hover:bg-n-ruby-3"
          @click="emit('deleteNode', node.id)"
        >
          <span class="i-lucide-trash-2 block" />
        </button>
        <button type="button" class="p-1 rounded text-n-slate-11 hover:bg-n-alpha-2" @click="emit('close')">
          <span class="i-lucide-x block" />
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-3 p-4 text-sm">
      <label class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.NODE_TITLE') }}</span>
        <input
          :value="data.title"
          type="text"
          class="!mb-0"
          @input="patch({ title: $event.target.value })"
        />
      </label>

      <!-- MESSAGE -->
      <template v-if="node.type === 'message'">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.MESSAGE_TEXT') }}</span>
          <textarea
            :value="config.text"
            rows="5"
            class="!mb-0"
            @input="patchConfig({ text: $event.target.value })"
          />
        </label>
        <p class="text-xs text-n-slate-10">{{ $t('FLOWS.BUILDER.PANEL.NEXT_BY_CONNECTION') }}</p>
      </template>

      <!-- WHATSAPP LIST -->
      <template v-if="node.type === 'whatsapp_list'">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.BODY') }}</span>
          <textarea :value="config.body" rows="4" class="!mb-0" @input="patchConfig({ body: $event.target.value })" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.FOOTER') }}</span>
          <input :value="config.footer" type="text" class="!mb-0" @input="patchConfig({ footer: $event.target.value })" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.BUTTON_TEXT') }}</span>
          <input :value="config.button_text" type="text" maxlength="20" class="!mb-0" @input="patchConfig({ button_text: $event.target.value })" />
        </label>

        <div v-for="(section, sIndex) in config.sections || []" :key="sIndex" class="rounded-lg border border-n-weak p-2 flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <input
              :value="section.title"
              type="text"
              maxlength="24"
              class="!mb-0 flex-1"
              :placeholder="$t('FLOWS.BUILDER.PANEL.SECTION_TITLE')"
              @input="updateSection(sIndex, { title: $event.target.value })"
            />
            <button type="button" class="p-1 text-n-ruby-11 hover:bg-n-ruby-3 rounded" @click="removeSection(sIndex)">
              <span class="i-lucide-trash-2 block text-sm" />
            </button>
          </div>

          <div v-for="(row, rIndex) in section.rows || []" :key="rIndex" class="rounded border border-n-weak p-2 flex flex-col gap-1 bg-n-solid-2">
            <div class="flex items-center gap-1">
              <input
                :value="row.title"
                type="text"
                maxlength="24"
                class="!mb-0 flex-1 text-sm"
                :placeholder="$t('FLOWS.BUILDER.PANEL.OPTION_TITLE')"
                @input="updateRow(sIndex, rIndex, { title: $event.target.value })"
                @blur="!row.id && updateRow(sIndex, rIndex, { id: autoId(row.title) })"
              />
              <button type="button" class="p-1 text-n-ruby-11 hover:bg-n-ruby-3 rounded" @click="removeRow(sIndex, rIndex)">
                <span class="i-lucide-x block text-sm" />
              </button>
            </div>
            <input
              :value="row.description"
              type="text"
              maxlength="72"
              class="!mb-0 text-xs"
              :placeholder="$t('FLOWS.BUILDER.PANEL.OPTION_DESCRIPTION')"
              @input="updateRow(sIndex, rIndex, { description: $event.target.value })"
            />
            <label class="flex items-center gap-1 text-[11px] text-n-slate-10">
              ID:
              <input
                :value="row.id"
                type="text"
                class="!mb-0 !py-0.5 text-[11px] flex-1"
                @change="updateRow(sIndex, rIndex, { id: slugify($event.target.value) })"
              />
            </label>
          </div>
          <button type="button" class="text-xs text-n-brand hover:underline text-left" @click="addRow(sIndex)">
            + {{ $t('FLOWS.BUILDER.PANEL.ADD_OPTION') }}
          </button>
        </div>
        <button type="button" class="text-xs text-n-brand hover:underline text-left" @click="addSection">
          + {{ $t('FLOWS.BUILDER.PANEL.ADD_SECTION') }}
        </button>
        <p class="text-xs text-n-slate-10">{{ $t('FLOWS.BUILDER.PANEL.TARGET_BY_CONNECTION') }}</p>
      </template>

      <!-- BUTTONS -->
      <template v-if="node.type === 'buttons'">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.BODY') }}</span>
          <textarea :value="config.body" rows="4" class="!mb-0" @input="patchConfig({ body: $event.target.value })" />
        </label>
        <div v-for="(button, bIndex) in config.buttons || []" :key="bIndex" class="rounded border border-n-weak p-2 flex flex-col gap-1 bg-n-solid-2">
          <div class="flex items-center gap-1">
            <input
              :value="button.title"
              type="text"
              maxlength="20"
              class="!mb-0 flex-1 text-sm"
              :placeholder="$t('FLOWS.BUILDER.PANEL.OPTION_TITLE')"
              @input="updateButton(bIndex, { title: $event.target.value })"
              @blur="!button.id && updateButton(bIndex, { id: autoId(button.title) })"
            />
            <button type="button" class="p-1 text-n-ruby-11 hover:bg-n-ruby-3 rounded" @click="removeButton(bIndex)">
              <span class="i-lucide-x block text-sm" />
            </button>
          </div>
          <label class="flex items-center gap-1 text-[11px] text-n-slate-10">
            ID:
            <input
              :value="button.id"
              type="text"
              class="!mb-0 !py-0.5 text-[11px] flex-1"
              @change="updateButton(bIndex, { id: slugify($event.target.value) })"
            />
          </label>
        </div>
        <button
          v-if="(config.buttons || []).length < 3"
          type="button"
          class="text-xs text-n-brand hover:underline text-left"
          @click="addButton"
        >
          + {{ $t('FLOWS.BUILDER.PANEL.ADD_BUTTON') }}
        </button>
        <p v-else class="text-xs text-n-amber-11">{{ $t('FLOWS.BUILDER.PANEL.BUTTONS_LIMIT') }}</p>
        <p class="text-xs text-n-slate-10">{{ $t('FLOWS.BUILDER.PANEL.TARGET_BY_CONNECTION') }}</p>
      </template>

      <!-- MEDIA -->
      <template v-if="node.type === 'media'">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.MEDIA_TYPE') }}</span>
          <select :value="config.media_type || 'image'" @change="onMediaTypeChange($event.target.value)">
            <option value="image">{{ $t('FLOWS.BUILDER.MEDIA_TYPES.IMAGE') }}</option>
            <option value="audio">{{ $t('FLOWS.BUILDER.MEDIA_TYPES.AUDIO') }}</option>
            <option value="video">{{ $t('FLOWS.BUILDER.MEDIA_TYPES.VIDEO') }}</option>
            <option value="file">{{ $t('FLOWS.BUILDER.MEDIA_TYPES.FILE') }}</option>
          </select>
        </label>
        <div
          v-if="config.media_asset_id"
          class="flex items-center justify-between gap-2 rounded border border-n-weak bg-n-solid-2 p-2"
        >
          <span class="text-xs truncate text-n-slate-12">{{ config.filename }}</span>
          <button type="button" class="p-1 text-n-ruby-11 hover:bg-n-ruby-3 rounded" @click="removeMedia">
            <span class="i-lucide-x block text-sm" />
          </button>
        </div>
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">
            {{ isUploading ? $t('FLOWS.BUILDER.PANEL.MEDIA_UPLOADING') : $t('FLOWS.BUILDER.PANEL.MEDIA_UPLOAD') }}
          </span>
          <input
            type="file"
            :accept="MEDIA_ACCEPT[config.media_type || 'image']"
            :disabled="isUploading"
            class="!mb-0 text-xs"
            @change="uploadMedia"
          />
        </label>
        <label v-if="(config.media_type || 'image') !== 'audio'" class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.MEDIA_CAPTION') }}</span>
          <textarea :value="config.caption" rows="2" class="!mb-0" @input="patchConfig({ caption: $event.target.value })" />
        </label>
        <p class="text-xs text-n-slate-10">{{ $t('FLOWS.BUILDER.PANEL.NEXT_BY_CONNECTION') }}</p>
      </template>

      <!-- HANDOFF -->
      <template v-if="node.type === 'handoff'">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.HANDOFF_MESSAGE') }}</span>
          <textarea :value="config.message" rows="3" class="!mb-0" @input="patchConfig({ message: $event.target.value })" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.LABELS') }}</span>
          <input :value="labelsText" type="text" class="!mb-0" placeholder="humano, vip" @change="updateLabels($event.target.value)" />
        </label>
        <p class="text-xs text-n-slate-10">{{ $t('FLOWS.BUILDER.PANEL.HANDOFF_HINT') }}</p>
      </template>

      <!-- END -->
      <template v-if="node.type === 'end'">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.END_MESSAGE') }}</span>
          <textarea :value="config.text" rows="3" class="!mb-0" @input="patchConfig({ text: $event.target.value })" />
        </label>
        <label class="flex items-center gap-2">
          <input
            type="checkbox"
            :checked="config.resolve_conversation"
            class="!mb-0"
            @change="patchConfig({ resolve_conversation: $event.target.checked })"
          />
          <span class="text-xs text-n-slate-11">{{ $t('FLOWS.BUILDER.PANEL.RESOLVE_CONVERSATION') }}</span>
        </label>
      </template>
    </div>
  </aside>
</template>
